import React from "react";

import getUrl from "Libs/getUrl";
import { notifyWithPriorityHandler as notify } from "src/common/containers/Notifications/util";

import type { IntlShape } from "react-intl";
import type { AgentRun } from "Reducers/agentRuns";
import type { AppDispatch } from "Store/configureStore";

interface NotifyOptions {
  run: AgentRun;
  dispatch: AppDispatch;
  intl: IntlShape;
}

/**
 * Both toasts link to the agent activity's own log. The Report tab defaults on
 * for an agent activity (`LogModal`), so one URL serves "Open activity" while
 * the run is in flight and "View the report" once it is done.
 */
const getActivityLogUrl = (run: AgentRun) =>
  getUrl({
    key: "organization.project.environment.log",
    props: {
      organizationId: run.organizationId,
      projectId: run.projectId,
      environmentId: run.environmentId,
      activityId: run.activityId
    }
  });

/**
 * "The agent is running, you can leave." Fired when the panel goes away with a
 * run still in flight — see `TroubleshootPanel` for why that is an unmount and
 * not a close handler.
 *
 * Keeps the `information` icon. `Notification.icon` can now show the design's
 * sparkle, as the log analyzer's started toast does; switching this one is a
 * design call.
 */
export const notifyTroubleshootingStarted = ({
  run,
  dispatch,
  intl
}: NotifyOptions) =>
  notify(dispatch).information({
    title: intl.formatMessage({
      id: "troubleshooting_agent.toast.started.title"
    }),
    description: intl.formatMessage({
      id: "troubleshooting_agent.toast.started.description"
    }),
    link: {
      title: intl.formatMessage({
        id: "troubleshooting_agent.toast.started.link"
      }),
      location: { to: getActivityLogUrl(run) },
      closeOnClick: true
    }
  });

/**
 * "Your report is ready." Fired by the run listener the moment a tracked run's
 * log parses to a report — which works with the panel and the log modal both
 * closed, because the stream worker is owned by a thunk closure rather than a
 * component and nothing terminates it.
 */
export const notifyTroubleshootingCompleted = ({
  run,
  dispatch,
  intl
}: NotifyOptions) =>
  notify(dispatch).success({
    title: intl.formatMessage({
      id: "troubleshooting_agent.toast.completed.title"
    }),
    description: intl.formatMessage(
      { id: "troubleshooting_agent.toast.completed.description" },
      { environmentName: <strong>{run.environmentName}</strong> }
    ),
    link: {
      title: intl.formatMessage({
        id: "troubleshooting_agent.toast.completed.link"
      }),
      location: { to: getActivityLogUrl(run) },
      closeOnClick: true
    }
  });
